import { trackEvent } from "@/engine/diagnostics/analytics";

import type { RoomMode } from "./roomControl";
import type { EntryRefusal } from "./roomAccess";
import { generateRoomCode } from "./roomIdentity";

/**
 * Opening a room, from the press that opens it.
 *
 * The press and the room are two addresses now (#610), so the code has to exist
 * before the visitor can be sent to the address that names it. Three things
 * happen here, in this order and for these reasons:
 *
 * 1. **The account is checked**, because it is the one answer that can refuse.
 *    The database will not accept an anonymous room, and being told so at the
 *    door is the difference between a refusal and a room that opens and then
 *    turns out not to be a room.
 * 2. **A code is generated.** Memorable, and settled here so the address the
 *    visitor lands on is the address they will share.
 * 3. **The row is written** — and a refusal does *not* bar the way in. The
 *    room works; its code admits nobody, which only the host can do anything
 *    about, and they are told inside the room where they can act on it.
 *
 * Framework-free and injectable, so the press can be tested without a router,
 * a session or a database.
 */
export interface RoomCreation {
  code: string;
  /** Null when the way in is open; the reason when it is not. */
  refusal: EntryRefusal | null;
  /** False when the row was refused. The walk goes ahead either way. */
  recorded: boolean;
}

export interface RoomCreationOptions {
  roomMode: RoomMode;
  /** The signed-in account, waited for — a sign-in may still be settling. */
  lookupAccount: () => Promise<string | null>;
  /**
   * Writes the row; answers false when the database refused it. It is handed
   * the account this press already waited for, so the row's owner is never a
   * second, racier reading of the same session.
   */
  note: (
    code: string,
    roomMode: RoomMode,
    ownerId: string,
    displayName?: string,
  ) => Promise<boolean | "taken">;
  /** What the host called the room, if anything. */
  displayName?: string;
  /** Only for tests: the code this room gets. */
  generate?: () => string;
}

export async function createRoomForEntry({
  roomMode,
  lookupAccount,
  note,
  displayName,
  generate = generateRoomCode,
}: RoomCreationOptions): Promise<RoomCreation> {
  const account = await lookupAccount();
  if (!account) {
    trackEvent("room-creation-refused");
    return { code: "", refusal: "needs-account", recorded: false };
  }
  // A code is drawn, not reserved: one that is already somebody's room is
  // answered "taken", and the press draws again rather than walking the visitor
  // into it (#764). The later draws carry a longer tail, so a crowded space
  // cannot hold the press for ever.
  for (let draw = 0; draw < DRAWS; draw++) {
    const code = drawCode(generate, draw);
    trackEvent("room-creation-started", { roomId: code });
    const recorded = await note(code, roomMode, account, displayName);
    if (recorded === "taken") {
      trackEvent("room-code-taken", { roomId: code });
      continue;
    }
    return { code, refusal: null, recorded };
  }
  // Every draw was somebody's room. Saying the way is closed is the honest
  // answer; entering one of theirs is the one thing this must not do.
  return { code: "", refusal: "room-closed", recorded: false };
}

/** How many codes a press draws before it gives up. */
const DRAWS = 6;

function drawCode(generate: () => string, draw: number): string {
  const code = generate().trim().toLowerCase();
  if (draw < DRAWS / 2) return code;
  return `${code}${Math.floor(Math.random() * 1000)}`;
}
